import React, { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Text, Icon, Popover, useTheme } from '@Sage/hoi-poi-ui';
import classnames from 'classnames';
import { getLiteral } from 'utils/getLiteral';
import { logEvent } from 'utils/tracking';
import { getDateFromBackendISO, getLocalDate } from 'utils/dates';
import { getBackendBoolean } from 'utils/fm';
import {
    ACTIVITIES,
    AGENDA,
    COMPANIES,
    CONTACTS,
    OPPORTUNITIES,
    SALESORDERS,
} from 'constants/Entities';

import './styles.scss';

const MetadataPopover = memo(({ className, data, entity }) => {
    const theme = useTheme();

    const onPopoverIsVisible = useCallback(
        (isVisible) => {
            if (isVisible) {
                logEvent({
                    event: entity.trueName,
                    functionality: 'showMetadataInfo',
                });
            }
        },
        [entity.trueName],
    );

    const formatted = useMemo(() => {
        const auditReadOnlyDb = data?.audit?.readOnlyDb;
        const hasAuditReadOnlyDb =
            auditReadOnlyDb !== undefined && auditReadOnlyDb !== null && auditReadOnlyDb !== '';

        return {
            auditCreatedDate: data?.audit?.created?.date
                ? getDateFromBackendISO(data?.audit?.created?.date, 'L LT')
                : null,
            auditCreatedUser: data?.audit?.created?.user?.name,
            auditModifiedUser: data?.audit?.modified?.user?.name,
            auditModifiedDate: data?.audit?.modified?.date
                ? getDateFromBackendISO(data?.audit?.modified?.date, 'L LT')
                : null,
            importDate: data.importDate
                ? getLocalDate(data.importDate, 'DD/MM/YYYY HH:mm:ss', 'L LT')
                : null,
            hasReadOnly: hasAuditReadOnlyDb,
            readOnly: getBackendBoolean(auditReadOnlyDb),
        };
    }, [data]);

    const renderContent = useMemo(() => {
        if (!data) return null;

        const showLevel = data.level !== undefined && data.level !== -1 && data.level !== '';

        return (
            <div className="fm-popover-metadata__content">
                <div className="fm-popover-metadata__line">
                    <div className="fm-popover-metadata__line__label">
                        <Text color={theme.colors.utility.textSecondary}>
                            {getLiteral('label_identifier_metadata')}
                        </Text>
                    </div>

                    <div className="fm-popover-metadata__line__label">
                        <Text color={theme.colors.utility.textPrimary}>{data.id}</Text>
                    </div>
                </div>

                {data.extId && (
                    <div className="fm-popover-metadata__line">
                        <div className="fm-popover-metadata__line__label">
                            <Text color={theme.colors.utility.textSecondary}>
                                {getLiteral('label_extid')}
                            </Text>
                        </div>

                        <div className="fm-popover-metadata__line__label">
                            <Text color={theme.colors.utility.textPrimary}>{data.extId}</Text>
                        </div>
                    </div>
                )}

                {formatted.auditCreatedDate && (
                    <div className="fm-popover-metadata__line">
                        <div className="fm-popover-metadata__line__label">
                            <Text color={theme.colors.utility.textSecondary}>
                                {getLiteral('label_date_created')}
                            </Text>
                        </div>

                        <div className="fm-popover-metadata__line__value">
                            <Text color={theme.colors.utility.textPrimary}>
                                {formatted.auditCreatedDate}
                            </Text>
                        </div>
                    </div>
                )}

                {formatted.auditCreatedUser && (
                    <div className="fm-popover-metadata__line">
                        <div className="fm-popover-metadata__line__label">
                            <Text color={theme.colors.utility.textSecondary}>
                                {getLiteral('label_creator_user')}
                            </Text>
                        </div>

                        <div className="fm-popover-metadata__line__value">
                            <Text color={theme.colors.utility.textPrimary}>
                                {formatted.auditCreatedUser}
                            </Text>
                        </div>
                    </div>
                )}

                {formatted.importDate && (
                    <div className="fm-popover-metadata__line">
                        <div className="fm-popover-metadata__line__label">
                            <Text color={theme.colors.utility.textSecondary}>
                                {getLiteral('label_import_date')}
                            </Text>
                        </div>

                        <div className="fm-popover-metadata__line__value">
                            <Text color={theme.colors.utility.textPrimary}>
                                {formatted.importDate}
                            </Text>
                        </div>
                    </div>
                )}

                {formatted.auditModifiedDate && (
                    <div className="fm-popover-metadata__line">
                        <div className="fm-popover-metadata__line__label">
                            <Text color={theme.colors.utility.textSecondary}>
                                {getLiteral('label_date_modified')}
                            </Text>
                        </div>

                        <div className="fm-popover-metadata__line__value">
                            <Text color={theme.colors.utility.textPrimary}>
                                {formatted.auditModifiedDate}
                            </Text>
                        </div>
                    </div>
                )}

                {formatted.auditModifiedUser && (
                    <div className="fm-popover-metadata__line">
                        <div className="fm-popover-metadata__line__label">
                            <Text color={theme.colors.utility.textSecondary}>
                                {getLiteral('label_user_last_modification')}
                            </Text>
                        </div>

                        <div className="fm-popover-metadata__line__value">
                            <Text color={theme.colors.utility.textPrimary}>
                                {formatted.auditModifiedUser}
                            </Text>
                        </div>
                    </div>
                )}

                {data.environment && (
                    <div className="fm-popover-metadata__line">
                        <div className="fm-popover-metadata__line__label">
                            <Text color={theme.colors.utility.textSecondary}>
                                {getLiteral('label_environment')}
                            </Text>
                        </div>

                        <div className="fm-popover-metadata__line__value">
                            <Text color={theme.colors.utility.textPrimary}>{data.environment}</Text>
                        </div>
                    </div>
                )}

                {showLevel && (
                    <div className="fm-popover-metadata__line">
                        <div className="fm-popover-metadata__line__label">
                            <Text color={theme.colors.utility.textSecondary}>
                                {getLiteral('label_security_level')}
                            </Text>
                        </div>

                        <div className="fm-popover-metadata__line__value">
                            <Text color={theme.colors.utility.textPrimary}>{data.level}</Text>
                        </div>
                    </div>
                )}

                {data.isPublic && (
                    <div className="fm-popover-metadata__line">
                        <div className="fm-popover-metadata__line__label">
                            <Text color={theme.colors.utility.textSecondary}>
                                {getLiteral('label_is_public')}
                            </Text>
                        </div>

                        <div className="fm-popover-metadata__line__value">
                            <Text color={theme.colors.utility.textPrimary}>
                                {data.isPublic ? getLiteral('label_yes') : getLiteral('label_no')}
                            </Text>
                        </div>
                    </div>
                )}

                {data.isPrivate && (
                    <div className="fm-popover-metadata__line">
                        <div className="fm-popover-metadata__line__label">
                            <Text color={theme.colors.utility.textSecondary}>
                                {getLiteral('label_activity_is_private')}
                            </Text>
                        </div>

                        <div className="fm-popover-metadata__line__value">
                            <Text color={theme.colors.utility.textPrimary}>
                                {data.isPrivate ? getLiteral('label_yes') : getLiteral('label_no')}
                            </Text>
                        </div>
                    </div>
                )}

                {formatted.hasReadOnly && (
                    <div className="fm-popover-metadata__line">
                        <div className="fm-popover-metadata__line__label">
                            <Text color={theme.colors.utility.textSecondary}>
                                {getLiteral('label_read_only')}
                            </Text>
                        </div>

                        <div className="fm-popover-metadata__line__value">
                            <Text color={theme.colors.utility.textPrimary}>
                                {formatted.readOnly
                                    ? getLiteral('label_yes')
                                    : getLiteral('label_no')}
                            </Text>
                        </div>
                    </div>
                )}
            </div>
        );
    }, [data, formatted, theme.colors.utility.textPrimary, theme.colors.utility.textSecondary]);

    return (
        renderContent && (
            <Popover
                className="fm-popover-metadata"
                placement="bottom"
                content={renderContent}
                trigger={['hover']}
                onVisibleChange={onPopoverIsVisible}
            >
                <div className={classnames('fm-popover-metadata__icon', className)}>
                    <Icon name="info" color={theme.colors.grey[500]} />
                </div>
            </Popover>
        )
    );
});

MetadataPopover.propTypes = {
    className: PropTypes.string,
    data: PropTypes.shape({
        id: PropTypes.any,
        extid: PropTypes.string,
        fCreado: PropTypes.string, // 'DD/MM/YYYY hh:mm'
        fModificado: PropTypes.string, // 'DD/MM/YYYY hh:mm'
        userModificado: PropTypes.string,
    }),
    entity: PropTypes.oneOf([ACTIVITIES, AGENDA, COMPANIES, CONTACTS, OPPORTUNITIES, SALESORDERS]),
};

export default MetadataPopover;
